import { IAcademicPeriod } from 'app/main/academic-period-tree';
import { Employee } from 'app/main/employees';
import { School } from 'app/main/schools/model/school.model';
import { IStudent } from 'app/main/students';

import { Privilege } from './privilege.model';
import { ITenant, Tenant } from './tenant.model';

export class UserTenantItem {
    public id?: string;
    public name?: string;
    public selected?: boolean;
}

export const UserUI = {
    DEFAULT: 'DEFAULT',
    STUDENT: 'STUDENT',
    TEACHER: 'TEACHER',
    PARENT: 'PARENT',
    NONE: 'NONE',
} as const;

export type UserUI = (typeof UserUI)[keyof typeof UserUI];

export class UserProperty {
    constructor(
        public key: string,
        public value: string,
        public active?: boolean,
        public locked?: boolean
    ) {}
}

export class UserPropertyVM {
    constructor(
        public key: string,
        public values: string[],
        public active?: boolean,
        public locked?: boolean
    ) {}
}

export interface RemovePropertyValueVM {
    key: string;
    value: string;
    ids: string[];
}

export interface UserImageBase64VM {
    username: string;
    imageBase64: string;
}

export interface IUser {
    id?: string;
    username?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    activated?: boolean;
    langKey?: string;
    roles?: string[];
    createdBy?: string;
    createdDate?: Date;
    changedBy?: string;
    changedDate?: Date;
    password?: string;
    tenant?: ITenant;
    imageUrl?: string;
    image?: string;
    timeZone?: string;
    properties?: UserProperty[];
    schools?: School[];
    activeSchool?: School;
    privileges?: Privilege[];
    twoFA?: boolean;
    changePassword?: boolean;
    languages?: string[];
    tenantId?: string;
    tenants?: UserTenantItem[];
    ui?: UserUI;
    student?: IStudent;
    teacher?: Employee;
    academicYear?: IAcademicPeriod;
    activeParentChild?: IStudent;
    locale?: string;
}

export class User implements IUser {
    constructor(
        public id?: string,
        public username?: string,
        public firstName?: string,
        public lastName?: string,
        public email?: string,
        public activated?: boolean,
        public langKey?: string,
        public roles?: string[],
        public createdBy?: string,
        public createdDate?: Date,
        public changedBy?: string,
        public changedDate?: Date,
        public password?: string,
        public locked?: boolean,
        public tenant?: Tenant,
        public imageUrl?: string,
        public image?: string,
        public timeZone?: string,
        public properties?: UserProperty[],
        public schools?: School[],
        public activeSchool?: School,
        public privileges?: Privilege[],
        public academicYear?: IAcademicPeriod,
        public twoFA?: boolean,
        public changePassword?: boolean,
        public tenantId?: string,
        public tenants?: UserTenantItem[],
        public ui?: UserUI,
        public student?: IStudent,
        public teacher?: Employee,
        public activeParentChild?: IStudent,
        public locale?: string
    ) {
        this.id = id ? id : null;
        this.username = username ? username : null;
        this.firstName = firstName ? firstName : null;
        this.lastName = lastName ? lastName : null;
        this.email = email ? email : null;
        this.activated = activated ? activated : false;
        this.langKey = langKey ? langKey : null;
        this.roles = roles ? roles : null;
        this.createdBy = createdBy ? createdBy : null;
        this.createdDate = createdDate ? createdDate : null;
        this.changedBy = changedBy ? changedBy : null;
        this.changedDate = changedDate ? changedDate : null;
        this.password = password ? password : null;
        this.locked = locked ? locked : false;
        this.tenant = tenant ? tenant : new Tenant();
        this.imageUrl = imageUrl ? imageUrl : null;
        this.image = image ? image : null;
        this.timeZone = timeZone ? timeZone : 'UTC';
        this.properties = properties ? properties : [];
        this.schools = schools ? schools : [];
        this.activeSchool = activeSchool ? activeSchool : null;
        this.privileges = privileges ? privileges : [];
        this.academicYear = academicYear ? academicYear : null;
        this.twoFA = twoFA ? twoFA : false;
        this.changePassword = changePassword ? changePassword : false;
        this.tenantId = tenantId ? tenantId : null;
        this.tenants = tenants ? tenants : [];
        this.ui = this.ui ?? UserUI.DEFAULT;
        this.student = student ?? student;
        this.teacher = teacher ?? teacher;
        this.activeParentChild = activeParentChild ? activeParentChild : null;
        this.locale = locale ?? 'en';
    }
}

export class VerificationCode {
    constructor(
        public code: string,
        public username: string
    ) {}
}

export const DataType = {
    PROPERTY: 'PROPERTY',
    ROLE: 'ROLE',
    DATA: 'DATA',
} as const;

export type DataType = (typeof DataType)[keyof typeof DataType];

export class CacheItemData {
    constructor(
        public type: DataType,
        public key: string,
        public value: string
    ) {}
}
export class UserCache {
    constructor(
        public id: string,
        public firstName: string,
        public lastName: string,
        public username: string,
        public email: string,
        public tenantId: string,
        public connected: boolean,
        public data: CacheItemData[],
        public ui: UserUI,
        public langKey: string,
        public activated: boolean,
        public enabled: boolean,
        public locked: boolean,
        public twoFA: boolean,
        public changePassword: boolean,
        public roles: string[],
        public tenants: string[]
    ) {}
}
